import React from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, DownloadIcon, UsersIcon, CheckCircleIcon, ListChecksIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function PTPSummaryScreen({
  data,
  onBack,
  onNext,
  onExport
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-240">
      <div className="flex items-center gap-3 mb-6" data-id="element-241">
        <ShieldCheckIcon className="w-8 h-8 text-orange-500" data-id="element-242" />
        <h2 className="text-2xl font-bold text-white" data-id="element-243">Pre-Task Plan</h2>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-4 px-4 space-y-6" data-id="element-244">
        {/* Header Info */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3" data-id="element-245">
          <div className="grid grid-cols-2 gap-4" data-id="element-246">
            <div data-id="element-247">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-248">
                Project
              </p>
              <p className="text-white font-medium text-sm" data-id="element-249">{data.project}</p>
            </div>
            <div data-id="element-250">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-251">
                Date
              </p>
              <p className="text-white font-medium text-sm" data-id="element-252">{data.date}</p>
            </div>
          </div>
          <div data-id="element-253">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-254">
              Location
            </p>
            <p className="text-white font-medium text-sm" data-id="element-255">{data.location}</p>
          </div>
          <div className="pt-3 border-t border-slate-800" data-id="element-256">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-257">
              Task
            </p>
            <p className="text-orange-400 font-bold" data-id="element-258">
              Steel Beam Installation Using Mobile Crane
            </p>
          </div>
        </div>

        {/* Step-Based Hazard Analysis */}
        <section data-id="element-259">
          <div className="flex items-center gap-2 mb-3" data-id="element-260">
            <ListChecksIcon className="w-5 h-5 text-slate-400" data-id="element-261" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-262">
              Job Steps — Hazards & Controls
            </h3>
          </div>
          <div className="space-y-3" data-id="element-263">
            {data.steps.map((step, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-id="element-264">
                {/* Step header */}
                <div className="px-4 py-3 border-b border-slate-800 flex gap-3" data-id="element-265">
                  <span className="text-orange-500 font-bold text-sm flex-shrink-0" data-id="element-266">
                    {i + 1}.
                  </span>
                  <span className="text-white font-medium text-sm" data-id="element-267">
                    {step.description}
                  </span>
                </div>
                {/* Hazards & Controls for this step */}
                <div className="px-4 py-3 space-y-3" data-id="element-268">
                  {step.hazards.map((h, j) => <div key={j} className="space-y-2" data-id="element-269">
                      <div className="flex items-start gap-2" data-id="element-270">
                        <AlertTriangleIcon className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" data-id="element-271" />
                        <div className="flex-1 flex items-center gap-2" data-id="element-272">
                          <span className="text-red-200 text-xs" data-id="element-273">
                            {h.hazard}
                          </span>
                          {h.riskLevel && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${h.riskLevel === 'HIGH' ? 'bg-red-500/20 text-red-400' : h.riskLevel === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`} data-id="element-274">
                              {h.riskLevel}
                            </span>}
                        </div>
                      </div>
                      <div className="ml-5 space-y-1" data-id="element-275">
                        {h.controls.map((c, k) => <div key={k} className="flex items-start gap-2 text-green-100 text-xs" data-id="element-276">
                            <CheckCircleIcon className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" data-id="element-277" />
                            <span data-id="element-278">{c}</span>
                          </div>)}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </section>

        {/* Crew */}
        <section data-id="element-279">
          <div className="flex items-center gap-2 mb-3" data-id="element-280">
            <UsersIcon className="w-5 h-5 text-slate-400" data-id="element-281" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-282">
              Crew ({data.crewSize})
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-283">
            {data.crewRoles.map((role, i) => <div key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-284">
                {role.role}
              </div>)}
          </div>
        </section>

        {/* PPE */}
        <section data-id="element-285">
          <div className="flex items-center gap-2 mb-3" data-id="element-286">
            <ShieldCheckIcon className="w-5 h-5 text-green-400" data-id="element-287" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-288">
              Required PPE
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-289">
            {data.ppeControls.map((ppe, i) => <div key={i} className="bg-green-950/20 border border-green-900/30 text-green-100 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-290">
                {ppe}
              </div>)}
          </div>
        </section>

        {/* Equipment */}
        <section data-id="element-291">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-292">
            Equipment
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-293">
            {data.equipment.map((item, i) => <div key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-294">
                {item}
              </div>)}
          </div>
        </section>

        {/* Safety Checks */}
        <section data-id="element-295">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-296">
            Pre-Work Safety Checks
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2" data-id="element-297">
            {data.safetyChecks.map((check, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-298">
                <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-299" />
                {check}
              </div>)}
          </div>
        </section>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-300">
        <PrimaryButton onClick={onNext} icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-302" />} data-id="element-301">
          Finalize & Continue
        </PrimaryButton>
        <SecondaryButton onClick={() => onExport?.()} icon={<DownloadIcon className="w-5 h-5" data-id="element-304" />} data-id="element-303">
          Export PTP
        </SecondaryButton>
        <SecondaryButton onClick={onBack} data-id="element-305">Edit Plan</SecondaryButton>
      </div>
    </div>;
}